import {
  createColumnHelper,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import PaginatedTable from '../../Common/PaginatedTable';
import { HiXCircle } from 'react-icons/hi';
import { formatNumbersDisplayOnly } from '../../../Utils';
import { DeliveryItem } from '../../../Models/DeliveryItem';
import { DeliveryStatusEnum } from '../../../Models/DeliveryStatus';
import { ClientLocationAsset } from '../../../Models/ClientLocationAsset';
import { LatestDailyPrice } from '../../../Models/LatestDailyPrice';
import { InventoryTypeEnum } from '../../../Models/InventoryType';
import { UserLevelNames } from '../../../Models/UserLevel';
import { useAppSelector } from '../../../Reducers/store';
import { useState } from 'react';

export default function DeliveryItemListTable({
  data,
  deleteDeliveryItem,
  editDeliveryItem,
  deliveryStatus,
  editLocationAssetItem,
  latestDailyPrice,
}: {
  data: DeliveryItem[];
  deleteDeliveryItem(id: number): void;
  editDeliveryItem: (inv: DeliveryItem) => void;
  deliveryStatus: number;
  editLocationAssetItem: (asset: ClientLocationAsset | null) => void;
  latestDailyPrice: LatestDailyPrice | null;
}) {
  const columnHelper = createColumnHelper<DeliveryItem>();

  const { userModel } = useAppSelector((state) => state.user);
  const columns = [
    columnHelper.accessor((row) => row.inventoryType?.name, {
      id: 'inventoryTypeId',
      header: 'Name',
      cell: (info) => (
        <div
          className='cursor-pointer text-blue-600'
          onClick={() => editDeliveryItem(info.row.original)}
        >
          {info.getValue()}
        </div>
      ),
      sortingFn: 'alphanumeric',
      footer: (info) => info.column.id,
      // filterFn: 'includesString',
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.quantity, {
      id: 'quantity',
      header: 'Fuel',
      sortingFn: 'alphanumeric',
      footer: (info) => info.column.id,
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.quantity, {
      id: 'fuelPriceId',
      header: 'Price',
      sortingFn: 'alphanumeric',
      cell: (info) => (
        <div>
          {info.row.original.inventoryTypeId ===
          InventoryTypeEnum.OffRoadDyedDiesel
            ? latestDailyPrice?.dyedDieselPrice ?? ''
            : ''}
          {info.row.original.inventoryTypeId ===
          InventoryTypeEnum.OnRoadClearDiesel
            ? latestDailyPrice?.clearDieselPrice ?? ''
            : ''}
          {info.row.original.inventoryTypeId === InventoryTypeEnum.Gasoline
            ? latestDailyPrice?.gasolinePrice ?? ''
            : ''}
          {info.row.original.inventoryTypeId === InventoryTypeEnum.DEF
            ? latestDailyPrice?.defPrice ?? ''
            : ''}
        </div>
      ),
      footer: (info) => info.column.id,
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.quantity, {
      id: 'fuelPriceTotalId',
      header: 'Total',
      sortingFn: 'alphanumeric',
      cell: (info) => (
        <div>
          {info.row.original.inventoryTypeId ===
          InventoryTypeEnum.OffRoadDyedDiesel
            ? '$' +
              formatNumbersDisplayOnly(
                (info.row.original.quantity ?? 0) *
                  (latestDailyPrice?.dyedDieselPrice ?? 0)
              )
            : ''}
          {info.row.original.inventoryTypeId ===
          InventoryTypeEnum.OnRoadClearDiesel
            ? '$' +
              formatNumbersDisplayOnly(
                (info.row.original.quantity ?? 0) *
                  (latestDailyPrice?.clearDieselPrice ?? 0)
              )
            : ''}
          {info.row.original.inventoryTypeId === InventoryTypeEnum.Gasoline
            ? '$' +
              formatNumbersDisplayOnly(
                (info.row.original.quantity ?? 0) *
                  (latestDailyPrice?.gasolinePrice ?? 0)
              )
            : ''}
          {info.row.original.inventoryTypeId === InventoryTypeEnum.DEF
            ? '$' +
              formatNumbersDisplayOnly(
                (info.row.original.quantity ?? 0) *
                  (latestDailyPrice?.defPrice ?? 0)
              )
            : ''}
        </div>
      ),
      footer: (info) => info.column.id,
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.defQuantity, {
      id: 'defQuantity',
      header: 'DEF',
      sortingFn: 'alphanumeric',
      footer: (info) => info.column.id,
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.quantity, {
      id: 'defPriceId',
      header: 'Price',
      sortingFn: 'alphanumeric',
      cell: (info) => (
        <div>
          {(info.row.original.defQuantity ?? 0) > 0
            ? latestDailyPrice?.defPrice ?? ''
            : ''}
        </div>
      ),
      footer: (info) => info.column.id,
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.quantity, {
      id: 'defPriceTotalId',
      header: 'Total',
      sortingFn: 'alphanumeric',
      cell: (info) => (
        <div>
          {(info.row.original.defQuantity ?? 0) > 0
            ? '$' +
              formatNumbersDisplayOnly(
                info.row.original.defQuantity! *
                  (latestDailyPrice?.defPrice ?? 0)
              )
            : ''}
        </div>
      ),
      footer: (info) => info.column.id,
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.clientLocationAssetId, {
      id: 'clientLocationAssetId',
      header: 'Asset',
      cell: (info) => (
        <div
          className='cursor-pointer text-blue-600'
          onClick={() =>
            editLocationAssetItem(info.row.original.clientLocationAsset)
          }
        >
          {info.row.original.clientLocationAsset?.number}
        </div>
      ),
      sortingFn: 'alphanumeric',
      footer: (info) => info.column.id,
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.clientLocationAssetId, {
      id: 'clientLocationAssetNotesId',
      header: 'Asset Type',
      cell: (info) => (
        <div
          className='cursor-pointer text-blue-600'
          onClick={() =>
            editLocationAssetItem(info.row.original.clientLocationAsset)
          }
        >
          {info.row.original.clientLocationAsset?.equipmentType?.name}
        </div>
      ),
      sortingFn: 'alphanumeric',
      footer: (info) => info.column.id,
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.id, {
      id: 'id',
      header: '',
      cell: (info) => (
        <div className='flex justify-center gap-1'>
          {deliveryStatus === DeliveryStatusEnum.InProcess && (
            <button
              type='button'
              className='buttonCancel p-2'
              onClick={() => deleteDeliveryItem(info.getValue())}
            >
              <HiXCircle />
            </button>
          )}
        </div>
      ),
      footer: (info) => info.column.id,
      size: 35,
      enableColumnFilter: false,
    }),
  ];

  const [columnVisibility, setColumnVisibility] = useState({
    //price: data.some(row => row.price > 100) // Show the price column if any row has price > 100
    fuelPriceId: userModel?.userLevelId === UserLevelNames.Admin,
    fuelPriceTotalId: userModel?.userLevelId === UserLevelNames.Admin,
    defPriceId: userModel?.userLevelId === UserLevelNames.Admin,
    defPriceTotalId: userModel?.userLevelId === UserLevelNames.Admin,
  });

  const table = useReactTable({
    data,
    columns,
    state: {
      columnVisibility,
    },
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 20,
      },
      sorting: [
        {
          id: 'id',
          desc: false,
        },
      ],
    },
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
  });

  return (
    <>
      <div className='w-full mx-auto overflow-x-auto'>
        <PaginatedTable
          table={table}
          dataCount={data.length}
          columnResizeMode='onChange'
          showFilters={true}
        />
      </div>
    </>
  );
}
